// src/pages/Homepage.jsx
import React, { useEffect, useMemo, useState } from "react";
import CountUp from "react-countup";
import { toast } from "react-toastify";
import API from "../../services/API";
import {
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Area,
  AreaChart,
} from "recharts";
import {
  FaUserCheck,
  FaUsers,
  FaChartLine,
  FaSchool,
  FaMapMarkerAlt,
  FaSortAmountDown,
  FaUserTimes,
  FaPercentage,
  FaExclamationTriangle,
  FaClipboardCheck,
} from "react-icons/fa";
import "react-circular-progressbar/dist/styles.css";

const isPlainObject = (value) =>
  value && typeof value === "object" && !Array.isArray(value);

const toArray = (data) => {
  if (Array.isArray(data)) return data;

  if (isPlainObject(data)) {
    const values = Object.values(data);

    if (values.every(Array.isArray)) {
      return values.flat();
    }

    return values;
  }

  return [];
};

const toNumberLike = (value, fallback = null) => {
  const number = Number(value);
  return Number.isFinite(number) ? number : fallback;
};

const avg = (numbers) => {
  const filtered = numbers.filter((number) => Number.isFinite(number));

  if (!filtered.length) return null;

  return filtered.reduce((sum, number) => sum + number, 0) / filtered.length;
};

const getApiData = (response) => response?.data || {};

const assertSuccess = (payload, fallbackMessage) => {
  if (!payload) {
    throw new Error(fallbackMessage || "Empty response");
  }

  if (payload.response_code !== undefined && payload.response_code !== 1000) {
    throw new Error(payload.message || fallbackMessage || "Request failed");
  }

  return payload.data ?? payload;
};

const normalizeTrend = (raw) => {
  let items = [];

  if (Array.isArray(raw)) {
    items = raw;
  } else if (isPlainObject(raw)) {
    const firstArray = Object.values(raw).find(Array.isArray);
    items = firstArray || [];
  }

  return items
    .map((item) => {
      const date = item.date || item.day || item.label || item.created_at || "";
      const rate = toNumberLike(
        item.attendance_rate ??
          item.rate ??
          item.value ??
          item.y ??
          item.percentage ??
          item.attendance_percentage,
        0
      );

      return {
        date,
        rate,
      };
    })
    .filter((item) => item.date);
};

const normalizeRecordingTrend = (raw) => {
  const items = Array.isArray(raw) ? raw : [];

  return items
    .map((item) => ({
      date: item.date || item.day || item.label || "",
      recorded_students: toNumberLike(item.recorded_students, 0),
      not_recorded_students: toNumberLike(
        item.not_recorded_students ?? item.unrecorded_students,
        0
      ),
      total_students: toNumberLike(item.total_students, 0),
      total_records: toNumberLike(item.total_records, 0),
      present_records: toNumberLike(item.present_records, 0),
      non_present_records: toNumberLike(item.non_present_records, 0),
      attendance_rate: toNumberLike(item.attendance_rate, 0),
      recording_rate: toNumberLike(item.recording_rate, 0),
    }))
    .filter((item) => item.date);
};

const normalizeOverview = (payload) => {
  const totalStudents = toNumberLike(payload?.total_students, 0);
  const recordedStudents = toNumberLike(payload?.recorded_students, 0);

  const notRecordedStudents = toNumberLike(
    payload?.not_recorded_students ?? payload?.unrecorded_students,
    0
  );

  const recordingRate = toNumberLike(
    payload?.recording_rate,
    totalStudents ? (recordedStudents / totalStudents) * 100 : 0
  );

  const notRecordedRate = toNumberLike(
    payload?.not_recorded_rate,
    totalStudents ? (notRecordedStudents / totalStudents) * 100 : 0
  );

  return {
    daerah: payload?.daerah || null,
    total_schools: toNumberLike(payload?.total_schools, 0),
    total_students: totalStudents,

    recorded_students: recordedStudents,
    not_recorded_students: notRecordedStudents,
    unrecorded_students: notRecordedStudents,

    recording_rate: recordingRate,
    not_recorded_rate: notRecordedRate,

    pending_students: toNumberLike(payload?.pending_students, 0),
    other_status_students: toNumberLike(payload?.other_status_students, 0),

    present_records: toNumberLike(payload?.present_records, 0),
    non_present_records: toNumberLike(payload?.non_present_records, 0),
    total_attendance_records: toNumberLike(payload?.total_attendance_records, 0),
    attendance_rate: toNumberLike(payload?.attendance_rate, 0),
  };
};

const getAttendanceTone = (rate) => {
  const value = toNumberLike(rate, 0);

  if (value >= 90) {
    return {
      label: "Excellent",
      color: "#059669",
      background: "rgba(16,185,129,0.12)",
    };
  }

  if (value >= 80) {
    return {
      label: "Healthy",
      color: "#2563eb",
      background: "rgba(59,130,246,0.12)",
    };
  }

  if (value >= 70) {
    return {
      label: "Watchlist",
      color: "#d97706",
      background: "rgba(245,158,11,0.15)",
    };
  }

  return {
    label: "Pending Action",
    color: "#dc2626",
    background: "rgba(239,68,68,0.12)",
  };
};

const getRecordingTone = (rate) => {
  const value = toNumberLike(rate, 0);

  if (value >= 90) {
    return {
      label: "Strong Coverage",
      color: "#059669",
      background: "rgba(16,185,129,0.12)",
    };
  }

  if (value >= 70) {
    return {
      label: "Moderate Coverage",
      color: "#d97706",
      background: "rgba(245,158,11,0.15)",
    };
  }

  return {
    label: "Pending Follow-Up",
    color: "#dc2626",
    background: "rgba(239,68,68,0.12)",
  };
};

const DashboardShell = ({ children }) => (
  <div
    className="min-vh-100 py-4"
    style={{
      background:
        "radial-gradient(circle at top left, rgba(99,102,241,0.20), transparent 34%), linear-gradient(135deg, #f8fafc 0%, #eef2ff 48%, #f5f3ff 100%)",
    }}
  >
    <div className="container-xl">{children}</div>
  </div>
);

const SectionCard = ({ children, className = "", style = {} }) => (
  <div
    className={`border-0 shadow-sm ${className}`}
    style={{
      background: "rgba(255,255,255,0.86)",
      border: "1px solid rgba(148,163,184,0.18)",
      borderRadius: 24,
      boxShadow: "0 20px 50px rgba(15,23,42,0.08)",
      backdropFilter: "blur(14px)",
      ...style,
    }}
  >
    {children}
  </div>
);

const PageHeader = ({ daerah, loading }) => (
  <SectionCard
    className="p-4 mb-4"
    style={{
      overflow: "hidden",
      position: "relative",
      background:
        "linear-gradient(135deg, rgba(15,23,42,0.96), rgba(49,46,129,0.92))",
      color: "#fff",
    }}
  >
    <div
      style={{
        position: "absolute",
        width: 260,
        height: 260,
        right: -80,
        top: -90,
        borderRadius: "50%",
        background: "rgba(129,140,248,0.24)",
      }}
    />

    <div
      style={{
        position: "absolute",
        width: 170,
        height: 170,
        right: 140,
        bottom: -90,
        borderRadius: "50%",
        background: "rgba(45,212,191,0.18)",
      }}
    />

    <div className="position-relative d-flex flex-wrap justify-content-between align-items-center">
      <div className="mb-3 mb-md-0">
        <div
          className="d-inline-flex align-items-center px-3 py-2 mb-3"
          style={{
            borderRadius: 999,
            background: "rgba(255,255,255,0.12)",
            border: "1px solid rgba(255,255,255,0.16)",
            fontSize: 13,
          }}
        >
          <FaMapMarkerAlt className="mr-2" />
          {loading ? "Resolving daerah…" : daerah || "PPD Dashboard"}
        </div>

        <h2
          className="mb-2 font-weight-bold"
          style={{ letterSpacing: "-0.04em" }}
        >
          Student Recording Intelligence
        </h2>

        <p
          className="mb-0"
          style={{ color: "rgba(255,255,255,0.72)", maxWidth: 720 }}
        >
          Monitor how many students have been recorded, how many still need
          follow-up, and which schools need support.
        </p>
      </div>

      <div
        className="text-md-right"
        style={{
          color: "rgba(255,255,255,0.75)",
          fontSize: 13,
        }}
      >
        <div>Live overview</div>
        <div style={{ color: "#fff", fontWeight: 700 }}>
          {new Date().toLocaleDateString("en-MY", {
            day: "2-digit",
            month: "short",
            year: "numeric",
          })}
        </div>
      </div>
    </div>
  </SectionCard>
);

const ModernStatCard = ({ icon, label, value, color, sublabel, loading }) => (
  <SectionCard className="p-4 h-100">
    <div className="d-flex justify-content-between align-items-start">
      <div
        className="d-inline-flex align-items-center justify-content-center"
        style={{
          width: 52,
          height: 52,
          color,
          background: `${color}18`,
          border: `1px solid ${color}28`,
          borderRadius: 18,
          fontSize: 24,
        }}
      >
        {icon}
      </div>

      <div
        style={{
          width: 44,
          height: 6,
          borderRadius: 999,
          background: `linear-gradient(90deg, ${color}, ${color}55)`,
        }}
      />
    </div>

    <div className="mt-4">
      <div
        className="font-weight-bold mb-1"
        style={{
          fontSize: 30,
          lineHeight: 1.1,
          letterSpacing: "-0.04em",
          color: "#0f172a",
        }}
      >
        {loading ? (
          <span className="text-muted" style={{ fontSize: 18 }}>
            Loading…
          </span>
        ) : (
          value
        )}
      </div>

      <div
        className="text-muted"
        style={{
          fontSize: 14,
          fontWeight: 600,
        }}
      >
        {label}
      </div>

      {sublabel && (
        <div
          className="mt-2"
          style={{
            fontSize: 12,
            color: "#64748b",
          }}
        >
          {sublabel}
        </div>
      )}
    </div>
  </SectionCard>
);

const EmptyState = ({ children }) => (
  <div
    className="text-muted d-flex align-items-center justify-content-center text-center px-3"
    style={{
      minHeight: 180,
      border: "1px dashed rgba(100,116,139,0.35)",
      borderRadius: 18,
      background:
        "linear-gradient(135deg, rgba(248,250,252,0.84), rgba(241,245,249,0.58))",
    }}
  >
    {children}
  </div>
);

const LoadingBar = () => (
  <div
    style={{
      height: 8,
      borderRadius: 999,
      background: "linear-gradient(90deg, #e2e8f0, #c7d2fe, #e2e8f0)",
      backgroundSize: "200% 100%",
    }}
  />
);

const PercentTooltip = ({ active, payload, label, suffix = "attendance" }) => {
  if (!active || !payload?.length) return null;

  return (
    <div
      style={{
        background: "rgba(15,23,42,0.92)",
        color: "#fff",
        padding: "10px 12px",
        borderRadius: 14,
        boxShadow: "0 18px 40px rgba(15,23,42,0.24)",
        border: "1px solid rgba(255,255,255,0.08)",
      }}
    >
      <div style={{ fontSize: 12, color: "rgba(255,255,255,0.68)" }}>
        {label}
      </div>
      <div style={{ fontWeight: 700 }}>
        {Number(payload[0].value || 0).toFixed(2)}% {suffix}
      </div>
    </div>
  );
};

const NumberTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;

  return (
    <div
      style={{
        background: "rgba(15,23,42,0.92)",
        color: "#fff",
        padding: "10px 12px",
        borderRadius: 14,
        boxShadow: "0 18px 40px rgba(15,23,42,0.24)",
        border: "1px solid rgba(255,255,255,0.08)",
      }}
    >
      <div style={{ fontSize: 12, color: "rgba(255,255,255,0.68)" }}>
        {label}
      </div>
      {payload.map((item) => (
        <div key={item.dataKey} style={{ fontWeight: 700 }}>
          {item.name}: {Number(item.value || 0).toLocaleString()}
        </div>
      ))}
    </div>
  );
};

const PeerSchoolsCard = ({ loading, daerah, schools, sort, setSort }) => {
  const sorted = useMemo(() => {
    const clone = [...(schools || [])];

    switch (sort) {
      case "name_asc":
        clone.sort((a, b) =>
          String(a.school_name || "").localeCompare(String(b.school_name || ""))
        );
        break;

      case "rate_asc":
        clone.sort(
          (a, b) =>
            toNumberLike(a.attendance_rate, 0) -
            toNumberLike(b.attendance_rate, 0)
        );
        break;

      case "rate_desc":
        clone.sort(
          (a, b) =>
            toNumberLike(b.attendance_rate, 0) -
            toNumberLike(a.attendance_rate, 0)
        );
        break;

      case "size_desc":
        clone.sort(
          (a, b) =>
            toNumberLike(b.total_students, 0) -
            toNumberLike(a.total_students, 0)
        );
        break;

      default:
        break;
    }

    return clone;
  }, [schools, sort]);

  return (
    <SectionCard className="p-4">
      <div className="d-flex flex-wrap justify-content-between align-items-center mb-4">
        <div className="mb-3 mb-md-0">
          <div className="d-flex align-items-center mb-1">
            <div
              className="d-inline-flex align-items-center justify-content-center mr-3"
              style={{
                width: 42,
                height: 42,
                borderRadius: 14,
                background: "rgba(245,158,11,0.14)",
                color: "#d97706",
              }}
            >
              <FaSchool />
            </div>
            <div>
              <h5
                className="mb-0 font-weight-bold"
                style={{ color: "#0f172a" }}
              >
                Schools in Your Daerah
              </h5>
              <div className="text-muted small">
                {daerah ? `Daerah: ${daerah}` : "No daerah resolved"}
              </div>
            </div>
          </div>
        </div>

        <div
          className="d-flex align-items-center px-3 py-2"
          style={{
            borderRadius: 14,
            background: "#f8fafc",
            border: "1px solid #e2e8f0",
          }}
        >
          <FaSortAmountDown className="mr-2 text-muted" />
          <select
            className="border-0 bg-transparent"
            value={sort}
            onChange={(event) => setSort(event.target.value)}
            style={{
              outline: "none",
              minWidth: 150,
              color: "#334155",
              fontWeight: 600,
              fontSize: 13,
            }}
          >
            <option value="rate_desc">Attendance ↑</option>
            <option value="rate_asc">Attendance ↓</option>
            <option value="name_asc">Name A→Z</option>
            <option value="size_desc">Student size</option>
          </select>
        </div>
      </div>

      {loading ? (
        <div>
          <LoadingBar />
          <div className="text-muted small mt-3">Loading schools…</div>
        </div>
      ) : !sorted.length ? (
        <EmptyState>No schools found.</EmptyState>
      ) : (
        <div className="table-responsive">
          <table
            className="table mb-0"
            style={{ borderCollapse: "separate", borderSpacing: "0 10px" }}
          >
            <thead>
              <tr
                style={{
                  color: "#64748b",
                  fontSize: 12,
                  textTransform: "uppercase",
                  letterSpacing: "0.05em",
                }}
              >
                <th className="border-0" style={{ width: 60 }}>
                  Rank
                </th>
                <th className="border-0">School</th>
                <th className="border-0 text-right">Students</th>
                <th className="border-0 text-right">Attendance</th>
                <th className="border-0 text-right">Status</th>
              </tr>
            </thead>

            <tbody>
              {sorted.map((school, index) => {
                const attendanceRate = toNumberLike(school.attendance_rate);
                const totalStudents = toNumberLike(school.total_students, 0);
                const tone = getAttendanceTone(attendanceRate);

                return (
                  <tr
                    key={school.school_id || school.school_uuid || index}
                    style={{
                      background: "#fff",
                      boxShadow: "0 8px 22px rgba(15,23,42,0.04)",
                    }}
                  >
                    <td
                      className="border-0 align-middle"
                      style={{
                        borderTopLeftRadius: 16,
                        borderBottomLeftRadius: 16,
                      }}
                    >
                      <span
                        className="d-inline-flex align-items-center justify-content-center"
                        style={{
                          width: 34,
                          height: 34,
                          borderRadius: 12,
                          background: index < 3 ? "#eef2ff" : "#f8fafc",
                          color: index < 3 ? "#4f46e5" : "#64748b",
                          fontWeight: 800,
                          fontSize: 13,
                        }}
                      >
                        {index + 1}
                      </span>
                    </td>

                    <td className="border-0 align-middle">
                      <div style={{ fontWeight: 800, color: "#0f172a" }}>
                        {school.school_name || school.name || "Unnamed school"}
                      </div>
                      <div className="text-muted small">
                        {school.school_id || school.school_uuid || "No school ID"}
                      </div>
                    </td>

                    <td
                      className="border-0 align-middle text-right"
                      style={{ color: "#334155", fontWeight: 700 }}
                    >
                      {Number(totalStudents || 0).toLocaleString()}
                    </td>

                    <td
                      className="border-0 align-middle text-right"
                      style={{ color: "#0f172a", fontWeight: 800 }}
                    >
                      {attendanceRate !== null
                        ? `${Number(attendanceRate).toFixed(2)}%`
                        : "—"}
                    </td>

                    <td
                      className="border-0 align-middle text-right"
                      style={{
                        borderTopRightRadius: 16,
                        borderBottomRightRadius: 16,
                      }}
                    >
                      <span
                        className="px-3 py-2"
                        style={{
                          borderRadius: 999,
                          background: tone.background,
                          color: tone.color,
                          fontWeight: 800,
                          fontSize: 12,
                        }}
                      >
                        {tone.label}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </SectionCard>
  );
};

const SchoolCoverageCard = ({ loading, schools }) => (
  <SectionCard className="p-4">
    <div className="d-flex flex-wrap justify-content-between align-items-start mb-4">
      <div>
        <h5 className="mb-1 font-weight-bold" style={{ color: "#0f172a" }}>
          Recording Percentage by School
        </h5>

        <div className="text-muted small">
          Shows how much of each school's student list has already been recorded.
        </div>

        <div
          className="mt-2 d-inline-flex px-3 py-2"
          style={{
            borderRadius: 999,
            background: "#f8fafc",
            border: "1px solid #e2e8f0",
            color: "#64748b",
            fontSize: 12,
            fontWeight: 700,
          }}
        >
          Recording % = Recorded Students ÷ Total Students
        </div>
      </div>
    </div>

    {loading ? (
      <div>
        <LoadingBar />
        <div className="text-muted small mt-3">Loading school recording data…</div>
      </div>
    ) : !schools.length ? (
      <EmptyState>No school recording percentage data available.</EmptyState>
    ) : (
      <div className="table-responsive">
        <table
          className="table mb-0"
          style={{ borderCollapse: "separate", borderSpacing: "0 10px" }}
        >
          <thead>
            <tr
              style={{
                color: "#64748b",
                fontSize: 12,
                textTransform: "uppercase",
                letterSpacing: "0.05em",
              }}
            >
              <th className="border-0">School</th>
              <th className="border-0 text-right">Total Students</th>
              <th className="border-0 text-right">Recorded</th>
              <th className="border-0 text-right">Still Pending</th>
              <th className="border-0 text-right">Recording %</th>
              <th className="border-0 text-right">Status</th>
            </tr>
          </thead>

          <tbody>
            {schools.map((school, index) => {
              const rate = toNumberLike(school.recording_rate, 0);
              const tone = getRecordingTone(rate);

              const totalStudents = toNumberLike(school.total_students, 0);
              const recordedStudents = toNumberLike(school.recorded_students, 0);
              const notRecordedStudents = toNumberLike(
                school.not_recorded_students ?? school.unrecorded_students,
                0
              );

              return (
                <tr
                  key={school.school_id || index}
                  style={{
                    background: "#fff",
                    boxShadow: "0 8px 22px rgba(15,23,42,0.04)",
                  }}
                >
                  <td
                    className="border-0 align-middle"
                    style={{
                      borderTopLeftRadius: 16,
                      borderBottomLeftRadius: 16,
                    }}
                  >
                    <div style={{ fontWeight: 800, color: "#0f172a" }}>
                      {school.school_name || "Unnamed school"}
                    </div>
                    <div className="text-muted small">
                      {school.daerah || "Unknown daerah"}
                    </div>
                  </td>

                  <td className="border-0 align-middle text-right font-weight-bold">
                    {Number(totalStudents || 0).toLocaleString()}
                  </td>

                  <td className="border-0 align-middle text-right font-weight-bold">
                    {Number(recordedStudents || 0).toLocaleString()}
                  </td>

                  <td className="border-0 align-middle text-right font-weight-bold">
                    {Number(notRecordedStudents || 0).toLocaleString()}
                  </td>

                  <td className="border-0 align-middle text-right">
                    <div style={{ fontWeight: 800, color: "#0f172a" }}>
                      {Number(rate || 0).toFixed(1)}%
                    </div>

                    <div
                      className="mt-2 ml-auto"
                      style={{
                        width: 120,
                        height: 8,
                        borderRadius: 999,
                        background: "#e2e8f0",
                        overflow: "hidden",
                      }}
                    >
                      <div
                        style={{
                          width: `${Math.max(0, Math.min(100, rate || 0))}%`,
                          height: "100%",
                          borderRadius: 999,
                          background: tone.color,
                        }}
                      />
                    </div>

                    <div className="text-muted small mt-1">
                      {Number(recordedStudents || 0).toLocaleString()} of{" "}
                      {Number(totalStudents || 0).toLocaleString()}
                    </div>
                  </td>

                  <td
                    className="border-0 align-middle text-right"
                    style={{
                      borderTopRightRadius: 16,
                      borderBottomRightRadius: 16,
                    }}
                  >
                    <span
                      className="px-3 py-2"
                      style={{
                        borderRadius: 999,
                        background: tone.background,
                        color: tone.color,
                        fontWeight: 800,
                        fontSize: 12,
                      }}
                    >
                      {tone.label}
                    </span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    )}
  </SectionCard>
);

const MonthlyAverageCard = ({ items }) => {
  if (!items.length) return null;

  return (
    <SectionCard className="p-4">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h5 className="mb-1 font-weight-bold" style={{ color: "#0f172a" }}>
            Monthly Average by Daerah
          </h5>
          <div className="text-muted small">
            Attendance comparison across available areas
          </div>
        </div>
      </div>

      <div className="row">
        {items.slice(0, 8).map((item, index) => {
          const rate = toNumberLike(
            item.monthly_average_attendance_rate ??
              item.average_attendance_rate ??
              item.avg ??
              item.rate,
            0
          );

          const tone = getAttendanceTone(rate);

          return (
            <div
              className="col-lg-3 col-md-6 mb-3"
              key={item.daerah || item.area || index}
            >
              <div
                className="p-3 h-100"
                style={{
                  borderRadius: 18,
                  background: "#fff",
                  border: "1px solid #e2e8f0",
                }}
              >
                <div className="text-muted small mb-2">
                  {item.daerah || item.area || "Unknown"}
                </div>

                <div
                  className="font-weight-bold"
                  style={{
                    fontSize: 24,
                    color: "#0f172a",
                  }}
                >
                  {rate !== null ? `${Number(rate).toFixed(1)}%` : "—"}
                </div>

                <div
                  className="mt-3"
                  style={{
                    height: 8,
                    borderRadius: 999,
                    background: "#e2e8f0",
                    overflow: "hidden",
                  }}
                >
                  <div
                    style={{
                      height: "100%",
                      width: `${Math.max(0, Math.min(100, rate || 0))}%`,
                      background: tone.color,
                      borderRadius: 999,
                    }}
                  />
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </SectionCard>
  );
};

const Homepage = () => {
  const [overview, setOverview] = useState({
    daerah: null,
    total_schools: 0,
    total_students: 0,
    recorded_students: 0,
    not_recorded_students: 0,
    unrecorded_students: 0,
    recording_rate: 0,
    not_recorded_rate: 0,
    pending_students: 0,
    other_status_students: 0,
    present_records: 0,
    non_present_records: 0,
    total_attendance_records: 0,
    attendance_rate: 0,
  });

  const [monthlyAverageOverall, setMonthlyAverageOverall] = useState(null);
  const [monthlyAverageItems, setMonthlyAverageItems] = useState([]);
  const [trend, setTrend] = useState([]);
  const [recordingTrend, setRecordingTrend] = useState([]);
  const [schoolCoverage, setSchoolCoverage] = useState([]);

  const [loading, setLoading] = useState({
    overview: false,
    monthlyAvg: false,
    trend: false,
    recordingTrend: false,
    coverage: false,
    peers: false,
  });

  const [peers, setPeers] = useState([]);
  const [peersMeta, setPeersMeta] = useState({
    daerah: null,
    total_schools: 0,
  });
  const [peerSort, setPeerSort] = useState("rate_desc");

  useEffect(() => {
    let alive = true;

    const headers = {
      "Content-Type": "application/json",
      Accept: "application/json",
      token: localStorage.getItem("token"),
    };

    const setLoadingKey = (key, value) => {
      if (!alive) return;

      setLoading((current) => ({
        ...current,
        [key]: value,
      }));
    };

    const loadOverview = async () => {
      setLoadingKey("overview", true);

      try {
        const response = await API.get("/ppdAnalytics/dashboard/overview", {
          headers,
        });

        const payload = assertSuccess(
          getApiData(response),
          "Failed to load dashboard overview"
        );

        if (!alive) return;

        const normalized = normalizeOverview(payload);
        setOverview(normalized);

        setPeersMeta((current) => ({
          ...current,
          daerah: normalized.daerah || current.daerah,
          total_schools: normalized.total_schools || current.total_schools,
        }));
      } catch (error) {
        if (!alive) return;
        toast.error(error?.message || "Failed to load dashboard overview");
      } finally {
        setLoadingKey("overview", false);
      }
    };

    const loadMonthlyAverage = async () => {
      setLoadingKey("monthlyAvg", true);

      try {
        const response = await API.get(
          "/ppdAnalytics/attendance/monthlyAverageByDaerah",
          { headers }
        );

        const payload = assertSuccess(
          getApiData(response),
          "Failed to load monthly averages"
        );

        const items = toArray(payload);

        const values = items
          .map((item) =>
            toNumberLike(
              item.monthly_average_attendance_rate ??
                item.average_attendance_rate ??
                item.avg ??
                item.rate
            )
          )
          .filter((number) => Number.isFinite(number));

        if (!alive) return;

        setMonthlyAverageItems(items);
        setMonthlyAverageOverall(avg(values));
      } catch (error) {
        if (!alive) return;

        setMonthlyAverageItems([]);
        setMonthlyAverageOverall(null);
        toast.error(error?.message || "Failed to load monthly averages");
      } finally {
        setLoadingKey("monthlyAvg", false);
      }
    };

    const loadDailyTrend = async () => {
      setLoadingKey("trend", true);

      try {
        const response = await API.get(
          "/ppdAnalytics/attendance/dailyTrendByDaerah",
          { headers }
        );

        const payload = assertSuccess(
          getApiData(response),
          "Failed to load daily trend"
        );

        if (!alive) return;

        setTrend(normalizeTrend(payload));
      } catch (error) {
        if (!alive) return;

        setTrend([]);
        toast.error(error?.message || "Failed to load daily trend");
      } finally {
        setLoadingKey("trend", false);
      }
    };

    const loadRecordingTrend = async () => {
      setLoadingKey("recordingTrend", true);

      try {
        const response = await API.get(
          "/ppdAnalytics/attendance/recordingTrend",
          { headers }
        );

        const payload = assertSuccess(
          getApiData(response),
          "Failed to load recording trend"
        );

        if (!alive) return;

        setRecordingTrend(normalizeRecordingTrend(payload));
      } catch (error) {
        if (!alive) return;

        setRecordingTrend([]);
        toast.error(error?.message || "Failed to load recording trend");
      } finally {
        setLoadingKey("recordingTrend", false);
      }
    };

    const loadSchoolCoverage = async () => {
      setLoadingKey("coverage", true);

      try {
        const response = await API.get(
          "/ppdAnalytics/attendance/schoolRecordingCoverage?limit=10&order=asc",
          { headers }
        );

        const payload = assertSuccess(
          getApiData(response),
          "Failed to load school recording percentage"
        );

        if (!alive) return;

        setSchoolCoverage(Array.isArray(payload) ? payload : []);
      } catch (error) {
        if (!alive) return;

        setSchoolCoverage([]);
        toast.error(error?.message || "Failed to load school recording percentage");
      } finally {
        setLoadingKey("coverage", false);
      }
    };

    const loadPeerSchools = async () => {
      setLoadingKey("peers", true);

      try {
        const response = await API.get(
          "/ppdAnalytics/schools/byDaerah?include_stats=true&exclude_self=true",
          { headers }
        );

        const payload = assertSuccess(
          getApiData(response),
          "Failed to load same-area schools"
        );

        if (!alive) return;

        setPeers(Array.isArray(payload.schools) ? payload.schools : []);
        setPeersMeta({
          daerah: payload.daerah || null,
          total_schools: payload.total_schools || 0,
        });
      } catch (error) {
        if (!alive) return;

        setPeers([]);
        setPeersMeta({
          daerah: null,
          total_schools: 0,
        });

        toast.error(error?.message || "Failed to load same-area schools");
      } finally {
        setLoadingKey("peers", false);
      }
    };

    Promise.allSettled([
      loadOverview(),
      loadMonthlyAverage(),
      loadDailyTrend(),
      loadRecordingTrend(),
      loadSchoolCoverage(),
      loadPeerSchools(),
    ]);

    return () => {
      alive = false;
    };
  }, []);

  const recordingTone = getRecordingTone(overview.recording_rate);

  return (
    <DashboardShell>
      <PageHeader
        daerah={overview.daerah || peersMeta.daerah}
        loading={loading.overview || loading.peers}
      />

      <div className="row mb-4">
        <div className="col-xl-3 col-lg-4 col-md-6 mb-4">
          <ModernStatCard
            icon={<FaUsers size={24} />}
            label="Total Students"
            loading={loading.overview}
            value={
              <CountUp
                end={overview.total_students || 0}
                separator=","
                duration={1.3}
              />
            }
            sublabel="All active students under your scope"
            color="#3B82F6"
          />
        </div>

        <div className="col-xl-3 col-lg-4 col-md-6 mb-4">
          <ModernStatCard
            icon={<FaClipboardCheck size={24} />}
            label="Recorded Students"
            loading={loading.overview}
            value={
              <CountUp
                end={overview.recorded_students || 0}
                separator=","
                duration={1.3}
              />
            }
            sublabel="Students already recorded in the system"
            color="#10B981"
          />
        </div>

        <div className="col-xl-3 col-lg-4 col-md-6 mb-4">
          <ModernStatCard
            icon={<FaUserTimes size={24} />}
            label="Not Recorded"
            loading={loading.overview}
            value={
              <CountUp
                end={overview.not_recorded_students || 0}
                separator=","
                duration={1.3}
              />
            }
            sublabel="Students who still need to be recorded"
            color="#EF4444"
          />
        </div>

        <div className="col-xl-3 col-lg-4 col-md-6 mb-4">
          <ModernStatCard
            icon={<FaPercentage size={24} />}
            label="Overall Recording %"
            loading={loading.overview}
            value={
              <CountUp
                end={overview.recording_rate || 0}
                decimals={1}
                duration={1.1}
                suffix="%"
              />
            }
            sublabel={`${Number(overview.recorded_students || 0).toLocaleString()} of ${Number(
              overview.total_students || 0
            ).toLocaleString()} students recorded`}
            color={recordingTone.color}
          />
        </div>

        <div className="col-xl-3 col-lg-4 col-md-6 mb-4">
          <ModernStatCard
            icon={<FaExclamationTriangle size={24} />}
            label="Still Pending %"
            loading={loading.overview}
            value={
              <CountUp
                end={overview.not_recorded_rate || 0}
                decimals={1}
                duration={1.1}
                suffix="%"
              />
            }
            sublabel={`${Number(
              overview.not_recorded_students || 0
            ).toLocaleString()} students still need recording`}
            color="#F97316"
          />
        </div>

        <div className="col-xl-3 col-lg-4 col-md-6 mb-4">
          <ModernStatCard
            icon={<FaSchool size={24} />}
            label="Total Schools"
            loading={loading.overview}
            value={
              <CountUp
                end={overview.total_schools || peersMeta.total_schools || 0}
                separator=","
                duration={1.1}
              />
            }
            sublabel={
              overview.daerah || peersMeta.daerah
                ? `Within ${overview.daerah || peersMeta.daerah}`
                : "Schools in your scope"
            }
            color="#F59E0B"
          />
        </div>

        <div className="col-xl-3 col-lg-4 col-md-6 mb-4">
          <ModernStatCard
            icon={<FaUserCheck size={24} />}
            label="Present Records"
            loading={loading.overview}
            value={
              <CountUp
                end={overview.present_records || 0}
                separator=","
                duration={1.2}
              />
            }
            sublabel="Students marked present in attendance"
            color="#14B8A6"
          />
        </div>

        <div className="col-xl-3 col-lg-4 col-md-6 mb-4">
          <ModernStatCard
            icon={<FaChartLine size={24} />}
            label="Monthly Avg Attendance"
            loading={loading.monthlyAvg}
            value={
              monthlyAverageOverall !== null ? (
                <CountUp
                  end={monthlyAverageOverall}
                  decimals={1}
                  duration={1.1}
                  suffix="%"
                />
              ) : (
                <span>--</span>
              )
            }
            sublabel="Average attendance across schools"
            color="#8B5CF6"
          />
        </div>
      </div>

      <div className="row">
        <div className="col-12 mb-4">
          <SectionCard className="p-4">
            <div className="d-flex flex-wrap justify-content-between align-items-center mb-4">
              <div>
                <h5
                  className="mb-1 font-weight-bold"
                  style={{ color: "#0f172a" }}
                >
                  Daily Attendance Trend
                </h5>
                <div className="text-muted small">
                  Attendance rate from attendance records, grouped by daerah.
                </div>
              </div>

              <div
                className="px-3 py-2 mt-3 mt-md-0"
                style={{
                  borderRadius: 999,
                  background: "#f8fafc",
                  color: "#64748b",
                  fontSize: 13,
                  fontWeight: 700,
                  border: "1px solid #e2e8f0",
                }}
              >
                {loading.trend
                  ? "Loading…"
                  : trend.length
                    ? `${trend.length} data points`
                    : "No data"}
              </div>
            </div>

            {loading.trend ? (
              <div>
                <LoadingBar />
                <div className="text-muted small mt-3">Loading trend…</div>
              </div>
            ) : trend.length ? (
              <div style={{ width: "100%", height: 360 }}>
                <ResponsiveContainer>
                  <AreaChart
                    data={trend}
                    margin={{ top: 10, right: 16, left: -10, bottom: 0 }}
                  >
                    <defs>
                      <linearGradient
                        id="attendanceGradient"
                        x1="0"
                        y1="0"
                        x2="0"
                        y2="1"
                      >
                        <stop
                          offset="5%"
                          stopColor="#6366f1"
                          stopOpacity={0.28}
                        />
                        <stop
                          offset="95%"
                          stopColor="#6366f1"
                          stopOpacity={0.02}
                        />
                      </linearGradient>
                    </defs>

                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis
                      dataKey="date"
                      tick={{ fontSize: 12, fill: "#64748b" }}
                      axisLine={false}
                      tickLine={false}
                    />
                    <YAxis
                      tick={{ fontSize: 12, fill: "#64748b" }}
                      domain={[0, 100]}
                      tickFormatter={(value) => `${value}%`}
                      axisLine={false}
                      tickLine={false}
                    />
                    <Tooltip content={<PercentTooltip suffix="attendance" />} />
                    <Area
                      type="monotone"
                      dataKey="rate"
                      stroke="#4f46e5"
                      strokeWidth={3}
                      fill="url(#attendanceGradient)"
                      activeDot={{ r: 5 }}
                    />
                    <Line
                      type="monotone"
                      dataKey="rate"
                      stroke="#4f46e5"
                      strokeWidth={3}
                      dot={false}
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <EmptyState>No attendance trend data available.</EmptyState>
            )}
          </SectionCard>
        </div>
      </div>

      <div className="row">
        <div className="col-12 mb-4">
          <SectionCard className="p-4">
            <div className="d-flex flex-wrap justify-content-between align-items-center mb-4">
              <div>
                <h5
                  className="mb-1 font-weight-bold"
                  style={{ color: "#0f172a" }}
                >
                  Daily Recording Volume
                </h5>
                <div className="text-muted small">
                  Daily view of students who have completed recording compared
                  with those still pending.
                </div>
              </div>

              <div
                className="px-3 py-2 mt-3 mt-md-0"
                style={{
                  borderRadius: 999,
                  background: "#f8fafc",
                  color: "#64748b",
                  fontSize: 13,
                  fontWeight: 700,
                  border: "1px solid #e2e8f0",
                }}
              >
                {loading.recordingTrend
                  ? "Loading…"
                  : recordingTrend.length
                    ? `${recordingTrend.length} data points`
                    : "No data"}
              </div>
            </div>

            {loading.recordingTrend ? (
              <div>
                <LoadingBar />
                <div className="text-muted small mt-3">
                  Loading recording trend…
                </div>
              </div>
            ) : recordingTrend.length ? (
              <div style={{ width: "100%", height: 340 }}>
                <ResponsiveContainer>
                  <AreaChart
                    data={recordingTrend}
                    margin={{ top: 10, right: 16, left: -10, bottom: 0 }}
                  >
                    <defs>
                      <linearGradient
                        id="recordingGradient"
                        x1="0"
                        y1="0"
                        x2="0"
                        y2="1"
                      >
                        <stop
                          offset="5%"
                          stopColor="#10b981"
                          stopOpacity={0.28}
                        />
                        <stop
                          offset="95%"
                          stopColor="#10b981"
                          stopOpacity={0.02}
                        />
                      </linearGradient>
                    </defs>

                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis
                      dataKey="date"
                      tick={{ fontSize: 12, fill: "#64748b" }}
                      axisLine={false}
                      tickLine={false}
                    />
                    <YAxis
                      tick={{ fontSize: 12, fill: "#64748b" }}
                      axisLine={false}
                      tickLine={false}
                    />
                    <Tooltip content={<NumberTooltip />} />
                    <Area
                      type="monotone"
                      dataKey="recorded_students"
                      name="Recorded Students"
                      stroke="#10b981"
                      strokeWidth={3}
                      fill="url(#recordingGradient)"
                      activeDot={{ r: 5 }}
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <EmptyState>No recording trend available.</EmptyState>
            )}
          </SectionCard>
        </div>
      </div>

      <div className="row">
        <div className="col-12 mb-4">
          <SchoolCoverageCard
            loading={loading.coverage}
            schools={schoolCoverage}
          />
        </div>
      </div>

      <div className="row">
        <div className="col-12 mb-4">
          <PeerSchoolsCard
            loading={loading.peers}
            daerah={overview.daerah || peersMeta.daerah}
            schools={peers}
            sort={peerSort}
            setSort={setPeerSort}
          />
        </div>
      </div>

      <div className="row">
        <div className="col-12 mb-4">
          <MonthlyAverageCard items={monthlyAverageItems} />
        </div>
      </div>
    </DashboardShell>
  );
};

export default Homepage;